import React from 'react';
import {
    instagramNewIcon,
    youtubeNewIcon,
    reelsIconInstagram,
    postsIconInstagram,
    reelsContentIcon,
    Story,
    Shorts
} from '../../../components/assets/icons';
import FallbackImg from '../../../resources/images/content_fallback.png';
import { Image } from '../../../components';
import Utils from '../../../common/utils';
import Icon from '../../../components/core_component/icon';

// Platform ID mapping
const PLATFORM_ID_MAP = {
    btll: 1, // Instagram
    yt: 2, // YouTube
    1: 1,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
};

// Content type mapping
const CONTENT_TYPE_MAP = {
    fxt1: { label: 'Image Post', icon: postsIconInstagram, isComponent: true },
    ge03: { label: 'Video Post', icon: reelsContentIcon, isComponent: true },
    utio: { label: 'Story', icon: Story, isComponent: true },
    uyts: { label: 'Story', icon: Story, isComponent: true },
    o5oi: { label: 'Reel', icon: reelsIconInstagram, isComponent: true },
    ttsn: { label: 'Video', icon: youtubeNewIcon, isComponent: true },
    vi82: { label: 'Shorts', icon: Shorts, isComponent: true },
};

export const normalizePlatformId = (platformId) => PLATFORM_ID_MAP[platformId] || platformId;

// Get platform info
const getPlatformInfo = (platformId) => {
    const normalizedId = normalizePlatformId(platformId);
    const platforms = {
        1: {
            name: 'Instagram',
            icon: instagramNewIcon,
            isComponent: true,
            color: '#E4405F',
            gradient:
                'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
        },
        2: {
            name: 'YouTube',
            icon: youtubeNewIcon,
            isComponent: true,
            color: '#FF0000',
            gradient: 'linear-gradient(45deg, #FF0000, #CC0000)',
        },
    };
    return (
        platforms[normalizedId] || {
            name: 'Unknown',
            icon: null,
            isComponent: false,
            color: '#6B7280',
            gradient: '#6B7280',
        }
    );
};

// Get content type info
const getContentTypeInfo = (contentTypeId) =>
    CONTENT_TYPE_MAP[contentTypeId] || { label: 'Content', icon: null, isComponent: false };

// Calculate engagement rate
const calculateER = (likes, comments, views) => {
    const total = (likes || 0) + (comments || 0);
    const viewCount = views || 0;
    if (viewCount === 0) return '-';
    const val = ((total / viewCount) * 100);
    if (val === 0) return '-';
    return Utils.formatEngagementRateDisplay(val);
};

// Generate content URL from shortcode
const generateContentUrl = (platformId, shortcode) => {
    const normalizedId = normalizePlatformId(platformId);
    if (normalizedId === 1) {
        return `https://www.instagram.com/p/${shortcode}/`;
    }
    if (normalizedId === 2) {
        return `https://www.youtube.com/watch?v=${shortcode}`;
    }
    return '#';
};

// Format large numbers
const formatNumber = (num) => {
    if (!num || num <= 0 || num === '0') return '-';
    if (num >= 1000000) {
        return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
        return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
};

const truncateText = (text, { maxChars = 80, maxWords = 10 } = {}) => {
    if (!text) return '';
    const trimmed = text.trim();
    const words = trimmed.split(/\s+/);
    if (maxWords && words.length > maxWords) {
        return `${words.slice(0, maxWords).join(' ')}...`;
    }
    if (maxChars && trimmed.length > maxChars) {
        return `${trimmed.slice(0, maxChars).trimEnd()}...`;
    }
    return trimmed;
};

const ContentCard = ({ item, onCardClick }) => {
    const platformInfo = getPlatformInfo(item.platform_id);
    const contentTypeInfo = getContentTypeInfo(item.content_type_id);
    const contentUrl = generateContentUrl(item.platform_id, item.shortcode);
    const er = (item.engagement_rate !== undefined && item.engagement_rate !== null)
        ? Utils.formatEngagementRateDisplay(item.engagement_rate)
        : calculateER(item.like_count, item.comment_count, item.views);

    // Generate thumbnail URL
    const getThumbnailUrl = () => {
        // 1. Try content_analytics thumbnail (specifically for this content_id)
        const analytics = item?.content_analytics?.[item.content_id] || {};
        if (analytics.thumbnail) return analytics.thumbnail;

        // 2. Try explicit thumbnail prop if not the default fallback or null
        if (item.thumbnail && typeof item.thumbnail === 'string' && !item.thumbnail.includes('no-img-found.webp')) return item.thumbnail;

        // 3. Try any thumbnail in content_analytics as a broad fallback
        if (item?.content_analytics) {
            const firstAnalytics = Object.values(item.content_analytics).find(a => a.thumbnail);
            if (firstAnalytics?.thumbnail) return firstAnalytics.thumbnail;
        }

        const normalizedId = normalizePlatformId(item.platform_id);
        if (normalizedId === 2 && item.shortcode) {
            return `https://img.youtube.com/vi/${item.shortcode}/hqdefault.jpg`;
        }

        // Final fallback: if we have a thumbnail string (even if it's the fallback one), return it.
        // If it's null/undefined, return the FallbackImg path.
        return item.thumbnail || FallbackImg;
    };

    const thumbnailUrl = getThumbnailUrl();

    const resolveOpenUrl = () => {
        const candidates = [];
        if (contentUrl && contentUrl !== '#') candidates.push(contentUrl);
        ['url', 'videoUrl', 'video_url', 'media_url', 'content_url', 'permalink', 'permalink_url', 'link'].forEach(k => {
            if (item && item[k]) candidates.push(item[k]);
        });
        // platform-specific shortcodes
        if (item && item.shortcode) {
            const pid = item.platform_id || item.platform || null;
            if (pid === 1 || pid === '1' || pid === 'btll') {
                candidates.push(`https://www.instagram.com/p/${item.shortcode}/`);
                candidates.push(`https://www.instagram.com/p/${item.shortcode}/`);
            }
            if (pid === 2 || pid === '2' || pid === 'yt') {
                candidates.push(`https://www.youtube.com/watch?v=${item.shortcode}`);
            }
        }
        const resolved = candidates.find(u => typeof u === 'string' && u && u !== '#') || null;
        console.log('ContentCard: resolveOpenUrl', { candidates, resolved });
        return resolved;
    };

    const handleCardClick = () => {
        const openUrl = resolveOpenUrl();
        console.log('ContentCard: handleCardClick', { item, openUrl });
        if (onCardClick) {
            console.log('ContentCard: calling onCardClick callback');
            try { onCardClick(item, openUrl); } catch (e) { console.error('ContentCard: onCardClick callback failed', e); }
        }
        // Also ensure the link opens even if popup blockers intervene with programmatic open
        if (openUrl) {
            console.log('ContentCard: internally attempting to open URL', openUrl);
            try {
                window.open(openUrl, '_blank', 'noopener,noreferrer');
            } catch (e) {
                console.error('ContentCard: internal window.open failed', e);
                try { window.open(openUrl, '_blank'); } catch (err) { /* ignore */ }
            }
        }
    };

    const hrefToUse = resolveOpenUrl();

    return (
        <a
            href={hrefToUse || '#'}
            target={hrefToUse ? "_blank" : undefined}
            rel={hrefToUse ? "noopener noreferrer" : undefined}
            style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
            onMouseDown={() => {
                // open on mouse down to maximize chance this is treated as a direct user gesture
                if (hrefToUse) {
                    try { window.open(hrefToUse, '_blank', 'noopener,noreferrer'); } catch (e) { try { window.open(hrefToUse, '_blank'); } catch (err) { /* ignore */ } }
                }
            }}
            onClick={(e) => {
                // If no resolved URL, prevent default navigation to '#' and still call handler
                if (!hrefToUse) e.preventDefault();
                handleCardClick();
            }}
        >
            <div className="content-grid-card" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column' }}>
                {/* Card Header */}
                <div className="content-card-header">
                    <div className="content-creator-info">
                        <Image
                            source={
                                item?.influencer_data?.profile_picture
                                    ? Utils.generateInfluencerDpUrl(
                                        item?.influencer_data?.influencer_id,
                                        item?.influencer_data?.profile_picture
                                    )
                                    : require("../../../resources/images/fallback.png")
                            }
                            alt={item?.influencer_data?.name || item.name || item.owner_name || 'Creator'}
                            className="content-creator-avatar"
                            style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '50%' }}
                            referrerPolicy="no-referrer"
                            crossOrigin="anonymous"
                            loading="lazy"
                            onError={(e) => {
                                if (!e.target.dataset.usedFallback) {
                                    e.target.dataset.usedFallback = 'true';
                                    e.target.src = FallbackImg;
                                }
                            }}
                        />
                        <div style={{ overflow: 'hidden' }}>
                            <span
                                className="content-creator-name"
                                style={{
                                    display: 'block',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: '#111827',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '160px',
                                }}
                            >
                                {item?.influencer_data?.name || item.owner_name || 'Influencer'}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                                {(item.followers !== undefined || item.influencer_data?.followers !== undefined) && (
                                    <span style={{ fontSize: '11px', color: '#6b7280' }}>
                                        {formatNumber(item.followers !== undefined ? item.followers : item.influencer_data?.followers)} Followers
                                    </span>
                                )}
                                {(item.content_posted_on || item.posted_on || item.date) && (
                                    <>
                                        {(item.followers !== undefined || item.influencer_data?.followers !== undefined) && (
                                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>•</span>
                                        )}
                                        <span style={{ fontSize: '11px', color: '#6b7280' }}>
                                            {typeof (item.content_posted_on || item.posted_on) === 'number'
                                                ? Utils.convertUnixToHumanSortReadable(item.content_posted_on || item.posted_on, 0, 0)
                                                : (item.date || item.content_posted_on || item.posted_on)}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <div
                        style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '20%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            flexShrink: 0,
                            overflow: 'hidden',
                        }}
                    >
                        {platformInfo.isComponent && platformInfo.icon
                            ? platformInfo.icon()
                            : platformInfo.icon}
                    </div>
                </div>

                <div
                    className="content-card-image"
                >
                    {thumbnailUrl ? (
                        <img
                            src={thumbnailUrl}
                            alt="Content thumbnail"
                            onError={(e) => {
                                if (e.target.src !== FallbackImg) {
                                    e.target.src = FallbackImg;
                                }
                            }}
                        />
                    ) : (
                        <img
                            src={FallbackImg}
                            alt="Content thumbnail"
                        />
                    )}

                    {/* Play Button Overlay */}
                    <div
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '56px',
                            height: '56px',
                            borderRadius: '50%',
                            background: 'rgba(0,0,0,0.6)',
                            display: thumbnailUrl ? 'flex' : 'none',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: 'opacity 0.2s',
                        }}
                        className="play-overlay"
                    >
                        <span style={{ color: 'white', fontSize: '24px', marginLeft: '4px' }}>▶</span>
                    </div>
                </div>

                {/* Card Footer */}
                <div className="content-card-footer" style={{ padding: '14px', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    {/* Content Type Badge */}
                    <div
                        style={{
                            display: 'flex',
                            gap: '8px',
                            marginBottom: '12px',
                            flexWrap: 'wrap',
                        }}
                    >
                        <span
                            style={{
                                background: `${platformInfo.color}15`,
                                color: platformInfo.color,
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                            }}
                        >
                            <span style={{ display: 'flex', alignItems: 'center', width: '14px', height: '14px' }}>
                                {contentTypeInfo.isComponent && contentTypeInfo.icon
                                    ? contentTypeInfo.icon({ width: '14', height: '14' })
                                    : contentTypeInfo.icon}
                            </span>
                            {contentTypeInfo.label}
                        </span>
                        <span
                            style={{
                                background: '#f3f4f6',
                                color: '#6b7280',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 500,
                            }}
                        >
                            Public
                        </span>
                    </div>

                    {/* Metrics Row */}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: '8px',
                            padding: '4px 0',
                        }}
                    >
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '2px' }}>Views</div>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#111827' }}>
                                {formatNumber(item.views)}
                            </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '2px' }}>Likes</div>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#111827' }}>
                                {formatNumber(item.like_count)}
                            </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '2px' }}>Comments</div>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#111827' }}>
                                {formatNumber(item.comment_count)}
                            </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '2px' }}>ER</div>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#111827' }}>
                                {er === '-' ? '-' : `${er}%`}
                            </div>
                        </div>
                    </div>

                    {/* Caption Preview */}
                    {item.caption && (
                        <div
                            className="content-card-caption-clip"
                            title={item.caption}
                            style={
                                item.hash_tags
                                    ? {
                                        marginBottom: '0',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }
                                    : {
                                        flex: 1,
                                        display: '-webkit-box',
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        WebkitLineClamp: 10,
                                        minHeight: 0,
                                        marginBottom: 0,
                                    }
                            }
                        >
                            {item.caption}
                        </div>
                    )}

                    {/* Hashtags */}
                    {item.hash_tags && (
                        <div
                            style={{
                                marginTop: '4px',
                                display: 'flex',
                                flexWrap: 'nowrap',
                                gap: '4px',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                width: '100%',
                                alignItems: 'center',
                            }}
                        >
                            {item.hash_tags
                                .split(',')
                                .slice(0, 3)
                                .map((tag, idx) => (
                                    <span
                                        key={idx}
                                        style={{
                                            fontSize: '11px',
                                            color: '#6b7280',
                                            background: '#f3f4f6',
                                            padding: '2px 8px',
                                            borderRadius: '10px',
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: '80px',
                                            display: 'inline-block',
                                        }}
                                    >
                                        {tag.trim()}
                                    </span>
                                ))}
                            {item.hash_tags.split(',').length > 3 && (
                                <span
                                    style={{
                                        fontSize: '11px',
                                        color: '#9ca3af',
                                        padding: '2px 8px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: '60px',
                                        display: 'inline-block',
                                    }}
                                >
                                    +{item.hash_tags.split(',').length - 3} more
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </a>
    );
};

export default ContentCard;
